import React, { useRef, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { IconFilter, IconX, IconSearch } from '@tabler/icons-react';
import styles from './toolbar.module.css';

interface ToolbarSearchProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Optional — called when Enter is pressed in the search field. */
  onSearchSubmit?: (value: string) => void;
  searchPlaceholder?: string;
  /**
   * Optional collapsible filter, paired to the left of the search inside the
   * same bordered group. Provide both to enable it (e.g. the work-item
   * dashboard's local filter); omit for a search-only control.
   */
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  filterPlaceholder?: string;
  /** Override the group's max width (px). Defaults to the kit's 700px. */
  maxWidth?: number;
  /**
   * Which input is the primary (always-open, grows to fill) control; the other
   * becomes the click-to-expand collapsible icon. Defaults to `'search'`. Only
   * meaningful when both a filter and a search are provided — set `'filter'`
   * to give the local filter priority (see the work-item dashboard).
   */
  primary?: 'search' | 'filter';
  /**
   * Opt-in narrow-screen mode: at or below this viewport width (px) the PRIMARY field
   * collapses to its icon too, so both start as icons and the one tapped expands to
   * fill the row (the bar wraps its other controls onto a second line while it is
   * open — see `.searchGroupCompact` in toolbar.module.css). Omit for the
   * unchanged behaviour; the work-item dashboard passes 768.
   */
  collapseBelow?: number;
}

/**
 * Search input with an optional collapsible filter, sharing one bordered
 * container (internal divider between them). The whole group lights up on
 * focus; while the collapsible field holds a value it also stays lit/expanded.
 * The filter always sits on the left and the search on the right — `primary`
 * only swaps which one grows to fill and which one collapses to an icon.
 */
export const ToolbarSearch: React.FC<ToolbarSearchProps> = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = 'Search…',
  filterValue,
  onFilterChange,
  filterPlaceholder = 'Filter…',
  maxWidth,
  primary = 'search',
  collapseBelow,
}) => {
  const hasFilter = filterValue !== undefined && onFilterChange !== undefined;
  const filterIsPrimary = hasFilter && primary === 'filter';
  const filterRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Narrow-screen mode collapses the primary field as well. A query that never
  // matches keeps the hook unconditional when the prop is omitted.
  const narrow = useMediaQuery(
    collapseBelow !== undefined ? `(max-width:${collapseBelow}px)` : '(max-width:0px)',
    { noSsr: true }
  );

  // Each field tracks its own expand state — normally only the non-primary one is
  // ever collapsible, but in narrow mode both are. Seeded open when the field already
  // holds a value, so pre-filled state isn't hidden.
  const [open, setOpen] = useState({
    filter: (filterValue?.length ?? 0) > 0,
    search: searchValue.length > 0,
  });
  const setFieldOpen = (field: 'filter' | 'search', value: boolean) =>
    setOpen((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
  const filterRole: 'primary' | 'collapsible' = filterIsPrimary && !narrow ? 'primary' : 'collapsible';
  const searchRole: 'primary' | 'collapsible' = !filterIsPrimary && !narrow ? 'primary' : 'collapsible';
  const anyCollapsibleOpen =
    (filterRole === 'collapsible' && hasFilter && open.filter) ||
    (searchRole === 'collapsible' && open.search);

  const handleClearFilter = (e: React.MouseEvent) => {
    // Prevent the input's blur firing before the clear so it stays expanded.
    e.preventDefault();
    onFilterChange?.('');
    filterRef.current?.focus();
  };

  const handleClearSearch = (e: React.MouseEvent) => {
    e.preventDefault();
    onSearchChange('');
    searchRef.current?.focus();
  };

  // Filter field — filter icon + clear button. Rendered as the primary (grow)
  // field or the collapsible (click-to-expand) icon per `role`.
  const renderFilter = (role: 'primary' | 'collapsible') => {
    const collapsible = role === 'collapsible';
    const isExpanded = !collapsible || open.filter;
    return (
      <div className={`${collapsible ? styles.filter : styles.search}${collapsible && isExpanded ? ` ${styles.filterExpanded}` : ''}`}>
        <span className={collapsible ? styles.filterIcon : styles.searchIcon}>
          <IconFilter size={16} stroke={1.5} />
        </span>
        <input
          ref={filterRef}
          className={collapsible ? styles.filterInput : styles.searchInput}
          type="text"
          value={filterValue}
          placeholder={isExpanded ? filterPlaceholder : ''}
          onChange={e => onFilterChange?.(e.target.value)}
          aria-label={filterPlaceholder}
          onFocus={collapsible ? () => setFieldOpen('filter', true) : undefined}
          onBlur={collapsible ? () => { if (!filterValue) setFieldOpen('filter', false); } : undefined}
          style={!collapsible && filterValue ? { paddingRight: 32 } : undefined}
        />
        {filterValue && (
          <button
            type="button"
            className={styles.filterClear}
            aria-label="Clear filter"
            onMouseDown={handleClearFilter}
          >
            <IconX size={16} stroke={1.5} />
          </button>
        )}
      </div>
    );
  };

  // Search field — search icon + Enter-to-submit. Primary (grow) or collapsible.
  const renderSearch = (role: 'primary' | 'collapsible') => {
    const collapsible = role === 'collapsible';
    const isExpanded = !collapsible || open.search;
    return (
      <div className={`${collapsible ? styles.filter : styles.search}${collapsible && isExpanded ? ` ${styles.filterExpanded}` : ''}`}>
        <span className={collapsible ? styles.filterIcon : styles.searchIcon}>
          <IconSearch size={16} stroke={1.5} />
        </span>
        <input
          ref={searchRef}
          className={collapsible ? styles.filterInput : styles.searchInput}
          type="text"
          value={searchValue}
          placeholder={isExpanded ? searchPlaceholder : ''}
          onChange={e => onSearchChange(e.target.value)}
          aria-label={searchPlaceholder}
          onFocus={collapsible ? () => setFieldOpen('search', true) : undefined}
          onBlur={collapsible ? () => { if (!searchValue) setFieldOpen('search', false); } : undefined}
          onKeyDown={e => {
            if (e.key === 'Enter') onSearchSubmit?.(searchValue);
          }}
        />
        {collapsible && searchValue && (
          <button
            type="button"
            className={styles.filterClear}
            aria-label="Clear search"
            onMouseDown={handleClearSearch}
          >
            <IconX size={16} stroke={1.5} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className={[
        styles.searchGroup,
        hasFilter && anyCollapsibleOpen ? styles.searchGroupActive : '',
        narrow ? styles.searchGroupCompact : '',
        narrow && anyCollapsibleOpen ? styles.searchGroupCompactOpen : '',
      ].filter(Boolean).join(' ')}
      style={maxWidth !== undefined && !narrow ? { maxWidth } : undefined}
    >
      {hasFilter && (
        <>
          {renderFilter(filterRole)}
          <div className={styles.groupDivider} />
        </>
      )}
      {renderSearch(searchRole)}
    </div>
  );
};
